/**
 * Cervus Botica — Phase 02 behavior layer
 */
(function () {
  const nav = document.querySelector('[data-editorial-nav]');
  const cintillo = document.querySelector('[data-editorial-cintillo]');
  const toggle = document.querySelector('[data-nav-toggle]');
  const panel = document.querySelector('[data-nav-panel]');
  const shell = document.querySelector('[data-nav-shell]');
  const body = document.body;
  const mobileOverlayMq = window.matchMedia('(max-width: 47.99rem)');

  function setChromeDrawerLock(type, open) {
    const classMap = {
      menu: ['menu-open', 'mobile-nav-open'],
      search: ['search-open', 'search-overlay-open', 'is-search-open'],
      cart: ['cart-open', 'cart-drawer-open'],
      account: ['account-open', 'account-overlay-open'],
    };

    (classMap[type] || []).forEach((className) => {
      body.classList.toggle(className, open);
    });
  }

  window.cervusBodyLock = { set: setChromeDrawerLock };

  function getMobileDrawerCloseMs() {
    const rootStyles = getComputedStyle(document.documentElement);
    const duration = Number.parseFloat(rootStyles.getPropertyValue('--mobile-chrome-overlay-duration')) || 420;
    return duration + 48;
  }

  function scheduleMobileDrawerCloseFinish(panel, onComplete) {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reducedMotion || !panel) {
      onComplete();
      return;
    }

    let finished = false;
    let timer = null;
    let transitionHandler = null;

    const complete = () => {
      if (finished) return;
      finished = true;

      if (transitionHandler) {
        panel.removeEventListener('transitionend', transitionHandler);
        transitionHandler = null;
      }

      if (timer) {
        window.clearTimeout(timer);
        timer = null;
      }

      onComplete();
    };

    transitionHandler = (event) => {
      if (event.target !== panel || event.propertyName !== 'transform') return;
      complete();
    };

    panel.addEventListener('transitionend', transitionHandler);
    timer = window.setTimeout(complete, getMobileDrawerCloseMs());
  }

  function openMobileChromeDrawer(root) {
    if (!root || root.classList.contains('is-open')) return;

    root.classList.remove('is-closing');
    window.requestAnimationFrame(() => {
      root.classList.add('is-open');
    });
  }

  function closeMobileChromeDrawer(root) {
    if (!root) return;
    root.classList.remove('is-open', 'is-closing');
  }

  function getMobileDrawerOpacityMs() {
    const rootStyles = getComputedStyle(document.documentElement);
    const duration = Number.parseFloat(rootStyles.getPropertyValue('--mobile-drawer-switch-opacity')) || 180;
    return Math.min(duration + 40, 260);
  }

  function getMobileDrawerSwitchDelayMs() {
    return 60;
  }

  function getMobileDrawerSwitchGuardMs() {
    return 260;
  }

  function beginMobileChromeDrawerClose(root) {
    if (!root || !root.classList.contains('is-open')) return;

    root.classList.add('is-closing');
    root.classList.remove('is-open');

    window.setTimeout(() => {
      root.classList.remove('is-closing');
    }, 220);
  }

  function closeMobileChromeDrawerAnimated(root) {
    beginMobileChromeDrawerClose(root);
    return Promise.resolve();
  }

  function waitMobileDrawerBeat() {
    return new Promise((resolve) => {
      window.setTimeout(resolve, getMobileDrawerSwitchDelayMs());
    });
  }

  function releaseMobileDrawerSwitchGuard() {
    if (switchGuardTimer) {
      window.clearTimeout(switchGuardTimer);
    }

    switchGuardTimer = window.setTimeout(() => {
      isSwitchingDrawer = false;
      body.classList.remove('mobile-drawer-switching');
      switchGuardTimer = null;
    }, getMobileDrawerSwitchGuardMs());
  }

  function armMobileDrawerSwitchGuard() {
    isSwitchingDrawer = true;
    body.classList.add('mobile-drawer-switching');
  }

  const mobileDrawerRegistry = {};
  let activeMobileDrawer = null;
  let isSwitchingDrawer = false;
  let switchGuardTimer = null;

  function detectActiveMobileDrawer() {
    if (!mobileOverlayMq.matches) return null;

    const order = ['cart', 'search', 'account', 'menu'];
    for (const type of order) {
      if (mobileDrawerRegistry[type]?.isOpen?.()) return type;
    }

    return null;
  }

  function releaseMobileChromeScrollLock() {
    if (document.body.dataset.cartScrollLocked === 'true') {
      window.cervusCart?.releaseScrollLock?.();
    }
  }

  async function openMobileDrawerManaged(type) {
    const controller = mobileDrawerRegistry[type];
    if (!controller?.open) return;

    window.cervusHeaderScroll?.show?.();
    syncMobileHeaderTotalHeight();
    syncNavSolidForOverlay(true);

    if (type !== 'cart') {
      setChromeDrawerLock(type, true);
    }

    await controller.open({ switchContext: false, managedOpen: true });

    if (type === 'cart') {
      setChromeDrawerLock(type, true);
    }

    activeMobileDrawer = type;
  }

  async function closeMobileDrawerManaged(type, options = {}) {
    const controller = mobileDrawerRegistry[type];
    if (!controller?.close) return;

    if (options.switchContext) {
      await controller.close({ switchContext: true });
      return;
    }

    await controller.close(options);
    setChromeDrawerLock(type, false);
  }

  async function closeMobileDrawerFully(type) {
    if (!mobileOverlayMq.matches || isSwitchingDrawer) return;
    if (!mobileDrawerRegistry[type]?.isOpen?.()) return;

    armMobileDrawerSwitchGuard();

    try {
      await closeMobileDrawerManaged(type, { switchContext: false });
      activeMobileDrawer = detectActiveMobileDrawer();

      if (!activeMobileDrawer) {
        releaseMobileChromeScrollLock();
        syncNavSolidForOverlay(false);
        activeMobileDrawer = null;
      }
    } finally {
      releaseMobileDrawerSwitchGuard();
    }
  }

  async function switchMobileDrawerManaged(from, to) {
    if (!mobileOverlayMq.matches || isSwitchingDrawer) return;
    if (from === to) return;

    armMobileDrawerSwitchGuard();

    try {
      window.cervusHeaderScroll?.show?.();
      syncMobileHeaderTotalHeight();
      syncNavSolidForOverlay(true);

      setChromeDrawerLock(from, false);

      if (to !== 'cart') {
        setChromeDrawerLock(to, true);
      }

      void closeMobileDrawerManaged(from, { switchContext: true });

      await waitMobileDrawerBeat();

      await mobileDrawerRegistry[to]?.open?.({ switchContext: true, managedOpen: true });

      if (to === 'cart') {
        setChromeDrawerLock(to, true);
      }

      activeMobileDrawer = to;
    } finally {
      releaseMobileDrawerSwitchGuard();
    }
  }

  async function toggleMobileDrawerManaged(type) {
    if (!mobileOverlayMq.matches) return false;
    if (isSwitchingDrawer) return false;

    const current = activeMobileDrawer ?? detectActiveMobileDrawer();

    if (current === type) {
      await closeMobileDrawerFully(type);
      return true;
    }

    if (current) {
      void switchMobileDrawerManaged(current, type);
      return true;
    }

    armMobileDrawerSwitchGuard();

    try {
      await openMobileDrawerManaged(type);
    } finally {
      releaseMobileDrawerSwitchGuard();
    }

    return true;
  }

  window.cervusMobileDrawer = {
    scheduleCloseFinish: scheduleMobileDrawerCloseFinish,
    getCloseMs: getMobileDrawerCloseMs,
    getOpacityMs: getMobileDrawerOpacityMs,
    getSwitchDelayMs: getMobileDrawerSwitchDelayMs,
    openVisual: openMobileChromeDrawer,
    closeVisual: closeMobileChromeDrawer,
    closeVisualAnimated: closeMobileChromeDrawerAnimated,
    beginClose: beginMobileChromeDrawerClose,
  };

  window.cervusMobileDrawerSwitch = {
    register(type, controller) {
      mobileDrawerRegistry[type] = controller;
    },
    toggle: toggleMobileDrawerManaged,
    close: closeMobileDrawerFully,
    getActive: () => activeMobileDrawer ?? detectActiveMobileDrawer(),
    isSwitching: () => isSwitchingDrawer,
  };

  function syncMobileHeaderTotalHeight() {
    if (!mobileOverlayMq.matches) return;
    const wrapper = document.querySelector('.header-wrapper');
    if (!wrapper) return;
    const height = Math.ceil(wrapper.getBoundingClientRect().height);
    if (height > 0) {
      document.documentElement.style.setProperty('--mobile-header-total-height', `${height}px`);
      document.documentElement.style.setProperty('--chrome-drawer-inset-top', `${height}px`);
    }
  }

  function isAnyChromeDrawerOpen() {
    return isAnyDrawerOpen();
  }

  if (shell && shell.parentElement !== document.body) {
    document.body.appendChild(shell);
  }

  const isIndex = body.classList.contains('template-index');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let scrollTicking = false;
  let heroObserver = null;

  window.addEventListener(
    'resize',
    () => {
      if (isAnyChromeDrawerOpen()) {
        syncMobileHeaderTotalHeight();
      }
    },
    { passive: true }
  );

  if (cintillo) {
    body.classList.add('has-announcement');
  }

  const headerGroup = document.querySelector('.shopify-section-group-header-group');

  function readChromeHeight() {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--site-chrome-height').trim();
    const parsed = parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : 100;
  }

  function isOverHero() {
    const hero = document.querySelector('#sala-00');
    if (!hero) return false;
    return hero.getBoundingClientRect().bottom > readChromeHeight() + 4;
  }

  function setNavLightHeader(isLight) {
    if (!nav) return;
    if (nav.classList.contains('header--static')) {
      nav.classList.add('is-light-header');
      return;
    }
    nav.classList.toggle('is-light-header', isLight);
  }

  function isMobileChromeOverlayOpen() {
    return isAnyDrawerOpen();
  }

  function isAnyDrawerOpen() {
    return (
      body.classList.contains('search-open') ||
      body.classList.contains('search-overlay-open') ||
      body.classList.contains('is-search-open') ||
      body.classList.contains('menu-open') ||
      body.classList.contains('mobile-nav-open') ||
      body.classList.contains('account-open') ||
      body.classList.contains('account-overlay-open')
    );
  }

  function restoreNavAfterDrawerClose() {
    if (!nav || isAnyDrawerOpen()) return;

    body.classList.remove(
      'drawer-open',
      'is-cart-open',
      'is-active',
      'overlay-open',
      'header-drawer-open',
      'header-light'
    );

    if (body.classList.contains('template-index')) {
      if (isOverHero()) {
        nav.classList.remove('editorial-nav--solid', 'is-light-header');
        body.classList.remove('editorial-nav-is-solid');
      } else {
        nav.classList.add('editorial-nav--solid', 'is-light-header');
        body.classList.add('editorial-nav-is-solid');
      }
      return;
    }

    if (nav.classList.contains('header--static')) {
      nav.classList.add('is-light-header');
      nav.classList.remove('editorial-nav--solid');
      body.classList.remove('editorial-nav-is-solid');
      return;
    }

    nav.classList.remove('editorial-nav--solid', 'is-light-header');
    body.classList.remove('editorial-nav-is-solid');
  }

  function syncNavSolidForOverlay(open) {
    if (!nav) return;

    if (open) {
      nav.classList.add('editorial-nav--solid', 'is-light-header');
      body.classList.remove('editorial-nav-is-solid');
      return;
    }

    restoreNavAfterDrawerClose();
  }

  window.cervusNavChrome = {
    syncSolid: syncNavSolidForOverlay,
    isMobileOverlayViewport: () => mobileOverlayMq.matches,
    syncMobileHeaderTotalHeight,
    restoreAfterDrawer: restoreNavAfterDrawerClose,
    isAnyDrawerOpen,
  };

  function setNavSolid(isSolid) {
    if (!nav || !isIndex) return;
    if (isMobileChromeOverlayOpen()) return;
    nav.classList.toggle('editorial-nav--solid', isSolid);
    body.classList.toggle('editorial-nav-is-solid', isSolid);
    setNavLightHeader(isSolid);
  }

  function syncNavToHeroPosition() {
    if (window.cervusCart?.isClosing?.()) return;
    if (!isIndex) return;
    setNavSolid(!isOverHero());
  }

  function initHeroHeaderState() {
    const hero = document.querySelector('#sala-00');
    if (!hero || !isIndex) return;

    if ('IntersectionObserver' in window) {
      const observeHero = () => {
        if (heroObserver) heroObserver.disconnect();

        heroObserver = new IntersectionObserver(
          ([entry]) => {
            setNavSolid(!entry.isIntersecting);
          },
          {
            root: null,
            rootMargin: `-${readChromeHeight()}px 0px 0px 0px`,
            threshold: 0,
          }
        );

        heroObserver.observe(hero);
      };

      observeHero();
      window.addEventListener('resize', observeHero, { passive: true });
    } else {
      window.addEventListener('scroll', syncNavToHeroPosition, { passive: true });
    }

    syncNavToHeroPosition();
  }

  function onScroll() {
    if (window.cervusCart?.isClosing?.()) return;
    if (!isIndex) return;
    if (scrollTicking) return;
    scrollTicking = true;
    window.requestAnimationFrame(() => {
      if (!heroObserver) syncNavToHeroPosition();
      scrollTicking = false;
    });
  }

  function initCloseHeader() {
    const closeSection = document.querySelector('#sala-08');
    if (!closeSection || !isIndex) return;

    const closeObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          body.classList.toggle('header-at-close', entry.isIntersecting && entry.intersectionRatio >= 0.12);
        });
      },
      { threshold: [0, 0.12, 0.35] }
    );

    closeObserver.observe(closeSection);
  }

  if (isIndex) {
    initHeroHeaderState();
    window.addEventListener('scroll', onScroll, { passive: true });
    initCloseHeader();
  }

  if (toggle && panel) {
    function resetMobileNavAccordion() {
      panel.querySelectorAll('[data-nav-accordion-trigger]').forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
        const panelId = trigger.getAttribute('aria-controls');
        const subPanel = panelId ? document.getElementById(panelId) : null;
        if (subPanel) subPanel.hidden = true;
      });
    }

    function closeNavPanel(options = {}) {
      const switchContext = options.switchContext === true;
      const immediate = options.immediate === true;
      const isMobileOpen = mobileOverlayMq.matches && shell?.classList.contains('is-open');

      if (panel.hidden && !isMobileOpen) return Promise.resolve();

      if (switchContext && isMobileOpen) {
        toggle.setAttribute('aria-expanded', 'false');
        panel.hidden = true;
        resetMobileNavAccordion();
        window.cervusMobileDrawer?.beginClose?.(shell);
        return Promise.resolve();
      }

      if (isMobileOpen) {
        toggle.setAttribute('aria-expanded', 'false');
        closeMobileChromeDrawer(shell);
        panel.hidden = true;
        setChromeDrawerLock('menu', false);
        resetMobileNavAccordion();
        if (!immediate) {
          syncNavSolidForOverlay(false);
        } else if (!window.cervusMobileDrawerSwitch?.getActive?.()) {
          syncNavSolidForOverlay(false);
        }
        return;
      }

      toggle.setAttribute('aria-expanded', 'false');
      panel.hidden = true;
      closeMobileChromeDrawer(shell);
      setChromeDrawerLock('menu', false);
      resetMobileNavAccordion();
      syncNavSolidForOverlay(false);
    }

    function openNavPanel(options = {}) {
      const switchContext = options.switchContext === true;

      if (!switchContext) {
        window.cervusSearch?.close?.({ immediate: true });
        window.cervusCart?.close?.({ immediate: true });
        window.cervusNavDrawer?.close?.();
        window.cervusAccount?.close?.({ immediate: true });
      }

      window.cervusHeaderScroll?.show?.();
      toggle.setAttribute('aria-expanded', 'true');
      panel.hidden = false;

      if (!switchContext) {
        setChromeDrawerLock('menu', true);
      }

      if (mobileOverlayMq.matches) {
        syncMobileHeaderTotalHeight();
        openMobileChromeDrawer(shell);
        if (!switchContext) {
          syncNavSolidForOverlay(true);
        }
      } else if (isIndex) {
        setNavSolid(true);
      }
    }

    toggle.addEventListener('click', () => {
      if (mobileOverlayMq.matches && window.cervusMobileDrawerSwitch) {
        window.cervusMobileDrawerSwitch.toggle('menu');
        return;
      }

      const isOpen = toggle.getAttribute('aria-expanded') === 'true';
      if (isOpen) {
        closeNavPanel();
      } else {
        openNavPanel();
      }
    });

    shell?.querySelectorAll('[data-nav-close]').forEach((target) => {
      target.addEventListener('click', () => {
        if (mobileOverlayMq.matches && window.cervusMobileDrawerSwitch) {
          window.cervusMobileDrawerSwitch.close('menu');
          return;
        }
        closeNavPanel();
      });
    });

    panel.querySelectorAll('.editorial-nav__accordion-sublink').forEach((link) => {
      link.addEventListener('click', () => {
        if (mobileOverlayMq.matches && window.cervusMobileDrawerSwitch) {
          window.cervusMobileDrawerSwitch.close('menu');
          return;
        }
        closeNavPanel();
      });
    });

    panel.querySelectorAll('[data-nav-shop-link], [data-nav-about-link]').forEach((link) => {
      link.addEventListener('click', () => {
        if (!mobileOverlayMq.matches) return;
        if (window.cervusMobileDrawerSwitch) {
          window.cervusMobileDrawerSwitch.close('menu');
          return;
        }
        closeNavPanel();
      });
    });

    panel.querySelectorAll('[data-nav-accordion-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        if (!mobileOverlayMq.matches) return;
        const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
        const panelId = trigger.getAttribute('aria-controls');
        const subPanel = panelId ? document.getElementById(panelId) : null;
        if (!subPanel) return;

        trigger.setAttribute('aria-expanded', String(!isExpanded));
        subPanel.hidden = isExpanded;
      });
    });

    document.addEventListener('click', (event) => {
      if (panel.hidden) return;
      if (mobileOverlayMq.matches) return;
      if (panel.contains(event.target) || toggle.contains(event.target)) return;
      closeNavPanel();
    });

    window.cervusNav = { close: (options) => closeNavPanel(options) };

    window.cervusMobileDrawerSwitch?.register?.('menu', {
      isOpen: () => mobileOverlayMq.matches && shell?.classList.contains('is-open') === true,
      open: async (options) => {
        openNavPanel(options);
      },
      close: async (options) => {
        await closeNavPanel(options);
      },
    });
  }

  function initMobileAccountOverlay() {
    const overlay = document.querySelector('[data-account-overlay]');
    const openTrigger = nav?.querySelector('[data-account-open]');
    if (!overlay || !openTrigger) return;

    const closeTargets = overlay.querySelectorAll('[data-account-close]');
    let lastFocus = null;
    let isOpen = false;

    function setAccountOpen(open, options = {}) {
      const switchContext = options.switchContext === true;
      if (!mobileOverlayMq.matches) return;

      if (open) {
        if (!switchContext) {
          window.cervusSearch?.close?.({ immediate: true });
          window.cervusCart?.close?.({ immediate: true });
          window.cervusNav?.close?.({ immediate: true });
          window.cervusNavDrawer?.close?.();
        }
        window.cervusHeaderScroll?.show?.();
        syncMobileHeaderTotalHeight();
      }

      if (!open && isOpen) {
        if (switchContext) {
          window.cervusMobileDrawer?.beginClose?.(overlay);
          overlay.setAttribute('aria-hidden', 'true');
          openTrigger.setAttribute('aria-expanded', 'false');
          isOpen = false;
          return Promise.resolve();
        }

        closeMobileChromeDrawer(overlay);
        overlay.setAttribute('aria-hidden', 'true');
        openTrigger.setAttribute('aria-expanded', 'false');
        isOpen = false;
        setChromeDrawerLock('account', false);
        syncNavSolidForOverlay(false);
        if (lastFocus instanceof HTMLElement) {
          lastFocus.focus();
        }
        return;
      }

      if (open && isOpen) return;

      isOpen = true;
      overlay.setAttribute('aria-hidden', 'false');
      if (!switchContext) {
        setChromeDrawerLock('account', true);
        syncNavSolidForOverlay(true);
      }
      openTrigger.setAttribute('aria-expanded', 'true');
      lastFocus = document.activeElement;
      openMobileChromeDrawer(overlay);
      overlay.querySelector('.mobile-chrome-overlay__close')?.focus();
    }

    openTrigger.addEventListener('click', (event) => {
      if (!mobileOverlayMq.matches) return;
      event.preventDefault();
      if (window.cervusMobileDrawerSwitch) {
        window.cervusMobileDrawerSwitch.toggle('account');
        return;
      }
      setAccountOpen(!isOpen);
    });

    closeTargets.forEach((target) => {
      target.addEventListener('click', () => {
        if (window.cervusMobileDrawerSwitch) {
          window.cervusMobileDrawerSwitch.close('account');
          return;
        }
        setAccountOpen(false);
      });
    });

    overlay.addEventListener('click', (event) => {
      if (event.target === overlay.querySelector('.mobile-chrome-overlay__backdrop')) {
        if (window.cervusMobileDrawerSwitch) {
          window.cervusMobileDrawerSwitch.close('account');
          return;
        }
        setAccountOpen(false);
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !isOpen) return;
      event.preventDefault();
      if (window.cervusMobileDrawerSwitch) {
        window.cervusMobileDrawerSwitch.close('account');
        return;
      }
      setAccountOpen(false);
    });

    overlay.querySelectorAll('a.account-overlay__link').forEach((link) => {
      link.addEventListener('click', () => {
        if (window.cervusMobileDrawerSwitch) {
          window.cervusMobileDrawerSwitch.close('account');
          return;
        }
        setAccountOpen(false);
      });
    });

    window.cervusAccount = {
      close: (options) => {
        if (!isOpen) return Promise.resolve();
        return Promise.resolve(setAccountOpen(false, options));
      },
    };

    window.cervusMobileDrawerSwitch?.register?.('account', {
      isOpen: () => isOpen,
      open: async (options) => {
        setAccountOpen(true, options);
      },
      close: async (options) => {
        if (isOpen) await Promise.resolve(setAccountOpen(false, options));
      },
    });
  }

  initMobileAccountOverlay();

  function initNavDropdowns() {
    if (!nav) return;

    const desktopMq = window.matchMedia('(min-width: 990px)');
    const drawerMq = window.matchMedia('(min-width: 48rem)');
    const dropdowns = nav.querySelectorAll('[data-nav-dropdown]');
    const drawer = document.querySelector('[data-nav-drawer]');
    const drawerPanels = drawer ? drawer.querySelectorAll('[data-nav-drawer-panel]') : [];

    if (!dropdowns.length || !drawer) return;

    const navSection =
      drawer.closest('.section-editorial-nav') || document.querySelector('.section-editorial-nav');
    const headerWrapper = document.querySelector('.header-wrapper');

    if (navSection && drawer.parentElement !== navSection) {
      navSection.appendChild(drawer);
    }

    let activePanelId = null;
    let closeTimer = null;
    let lastPointerX = Number.NaN;
    let lastPointerY = Number.NaN;
    const DROPDOWN_CLOSE_GRACE_MS = 200;

    function isInActiveDropdownSurface(node) {
      if (!node || !(node instanceof Node) || !activePanelId) return false;

      const activePanel = drawer?.querySelector(`#${CSS.escape(activePanelId)}`);
      const activeDropdown = Array.from(dropdowns).find((dropdown) => {
        const trigger = dropdown.querySelector('[data-nav-dropdown-trigger]');
        return trigger?.getAttribute('aria-controls') === activePanelId;
      });

      if (activePanel?.contains(node)) return true;
      if (activeDropdown?.contains(node)) return true;
      return false;
    }

    function isPointerInActiveSurface(clientX, clientY) {
      if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) return false;
      const target = document.elementFromPoint(clientX, clientY);
      return isInActiveDropdownSurface(target);
    }

    function shouldKeepDrawerOpen(event) {
      if (isInActiveDropdownSurface(event.relatedTarget)) return true;
      return isPointerInActiveSurface(event.clientX, event.clientY);
    }

    function cancelScheduledClose() {
      if (closeTimer === null) return;
      window.clearTimeout(closeTimer);
      closeTimer = null;
    }

    function scheduleClose() {
      if (!desktopMq.matches) return;
      cancelScheduledClose();
      closeTimer = window.setTimeout(() => {
        closeTimer = null;
        if (!desktopMq.matches) return;
        if (isPointerInActiveSurface(lastPointerX, lastPointerY)) return;
        if (isInActiveDropdownSurface(document.activeElement)) return;
        closeDrawer();
      }, DROPDOWN_CLOSE_GRACE_MS);
    }

    function setDropdownChrome(isOpen) {
      navSection?.classList.toggle('is-nav-dropdown-open', isOpen);
      headerGroup?.classList.toggle('is-nav-dropdown-open', isOpen);
      headerWrapper?.classList.toggle('is-nav-dropdown-open', isOpen);
      if (!isOpen) return;
      window.cervusHeaderScroll?.show?.();
      if (isIndex) setNavSolid(true);
    }

    function setDropdownState(panelId) {
      dropdowns.forEach((dropdown) => {
        const trigger = dropdown.querySelector('[data-nav-dropdown-trigger]');
        const controls = trigger?.getAttribute('aria-controls');
        const isActive = panelId && controls === panelId;
        dropdown.classList.toggle('is-open', isActive);
        trigger?.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      });
    }

    function openDrawer(panelId) {
      if (!drawerMq.matches || !drawer || !panelId) return;
      cancelScheduledClose();
      window.cervusSearch?.close?.();
      window.cervusNav?.close?.();
      activePanelId = panelId;
      drawer.hidden = false;
      drawer.classList.add('is-open');
      setDropdownChrome(true);
      drawerPanels.forEach((panel) => {
        panel.classList.toggle('is-active', panel.id === panelId);
      });
      setDropdownState(panelId);
    }

    function closeDrawer() {
      cancelScheduledClose();
      if (!drawer || drawer.hidden) return;
      activePanelId = null;
      drawer.hidden = true;
      drawer.classList.remove('is-open');
      setDropdownState('');
      drawerPanels.forEach((panel) => panel.classList.remove('is-active'));
      setDropdownChrome(false);
      if (isIndex) syncNavToHeroPosition();
    }

    function onHoverZoneLeave(event) {
      if (!desktopMq.matches) return;
      if (shouldKeepDrawerOpen(event)) {
        cancelScheduledClose();
        return;
      }
      scheduleClose();
    }

    function onDocumentPointerMove(event) {
      lastPointerX = event.clientX;
      lastPointerY = event.clientY;
      if (!desktopMq.matches || drawer.hidden) return;
      if (isInActiveDropdownSurface(event.target) || isPointerInActiveSurface(event.clientX, event.clientY)) {
        cancelScheduledClose();
        return;
      }
      scheduleClose();
    }

    dropdowns.forEach((dropdown) => {
      const trigger = dropdown.querySelector('[data-nav-dropdown-trigger]');
      if (!trigger) return;

      const panelId = trigger.getAttribute('aria-controls');

      dropdown.addEventListener('mouseleave', onHoverZoneLeave);

      trigger.addEventListener('mouseenter', () => {
        if (!desktopMq.matches) return;
        openDrawer(panelId);
      });

      trigger.addEventListener('focusin', () => {
        if (!drawerMq.matches) return;
        openDrawer(panelId);
      });

      trigger.addEventListener('focusout', (event) => {
        if (!drawerMq.matches) return;
        if (shouldKeepDrawerOpen(event)) return;
        closeDrawer();
      });

      trigger.addEventListener('click', (event) => {
        const href = trigger.getAttribute('href');
        const isNavLink = trigger.hasAttribute('data-nav-dropdown-link');

        // Primary nav links (Shop/About): never intercept — hover opens dropdown, click navigates.
        if (isNavLink && href && href !== '#') {
          return;
        }

        if (!drawerMq.matches) return;

        event.preventDefault();
        if (activePanelId === panelId && !drawer.hidden) {
          closeDrawer();
        } else {
          openDrawer(panelId);
        }
      });
    });

    drawerPanels.forEach((panel) => {
      panel.addEventListener('mouseleave', onHoverZoneLeave);
      panel.addEventListener('mouseenter', () => {
        if (!desktopMq.matches) return;
        cancelScheduledClose();
      });
    });

    document.addEventListener('pointermove', onDocumentPointerMove, { passive: true });

    document.addEventListener('click', (event) => {
      if (!drawerMq.matches || drawer.hidden) return;
      if (isInActiveDropdownSurface(event.target)) return;
      closeDrawer();
    });

    window.addEventListener(
      'scroll',
      () => {
        if (!drawerMq.matches || drawer.hidden) return;
        closeDrawer();
      },
      { passive: true }
    );

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (!drawer.hidden) {
        closeDrawer();
      }
      if (document.activeElement?.closest?.('[data-nav-dropdown]')) {
        document.activeElement.blur();
      }
    });

    const onDrawerMqChange = () => {
      if (!drawerMq.matches) closeDrawer();
    };

    if (typeof drawerMq.addEventListener === 'function') {
      drawerMq.addEventListener('change', onDrawerMqChange);
    } else if (typeof drawerMq.addListener === 'function') {
      drawerMq.addListener(onDrawerMqChange);
    }

    window.cervusNavDrawer = { close: closeDrawer };
  }

  initNavDropdowns();

  if (!reducedMotion && isIndex) {
    const revealables = document.querySelectorAll('.editorial-sala:not(.hero-editorial)');
    if (revealables.length) {
      revealables.forEach((section) => section.classList.add('editorial-sala--reveal'));

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                entry.target.classList.add('is-revealed');
                observer.unobserve(entry.target);
              }
            });
          },
          { rootMargin: '0px 0px -1% 0px', threshold: 0.015 }
        );

        revealables.forEach((section) => observer.observe(section));
      } else {
        revealables.forEach((section) => section.classList.add('is-revealed'));
      }
    }
  }

  document.querySelectorAll('.editorial-image__frame').forEach((frame) => {
    const reveal = () => frame.classList.add('is-visible');

    if (reducedMotion) {
      reveal();
      return;
    }

    if (frame.complete && frame.naturalWidth > 0) {
      reveal();
    } else {
      frame.addEventListener('load', reveal, { once: true });
      frame.addEventListener('error', reveal, { once: true });
    }
  });

  function initPlateReveal() {
    if (reducedMotion || !isIndex) return;
    const plates = document.querySelectorAll('.editorial-image__placeholder');
    if (!('IntersectionObserver' in window) || !plates.length) return;

    const plateObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          plateObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -4% 0px', threshold: 0.12 }
    );

    plates.forEach((plate) => {
      plate.classList.add('editorial-image__plate');
      plateObserver.observe(plate);
    });
  }

  function initMicroDepth() {
    if (reducedMotion || !isIndex) return;
    const frames = document.querySelectorAll('.editorial-sala:not(.hero-editorial) .editorial-image__frame');
    if (!frames.length) return;

    let depthTicking = false;

    function updateDepth() {
      frames.forEach((frame) => {
        const rect = frame.getBoundingClientRect();
        const viewMid = window.innerHeight * 0.5;
        const center = rect.top + rect.height * 0.5;
        const dist = (center - viewMid) / window.innerHeight;
        const shift = Math.max(-1, Math.min(1, dist)) * 1.15;
        frame.style.setProperty('--scroll-shift', shift.toFixed(2));
      });
      depthTicking = false;
    }

    window.addEventListener(
      'scroll',
      () => {
        if (depthTicking) return;
        depthTicking = true;
        window.requestAnimationFrame(updateDepth);
      },
      { passive: true }
    );

    updateDepth();
  }

  if (isIndex) {
    initPlateReveal();
    initMicroDepth();
  }
})();
